import { Navigate } from "react-router-dom";
import { useStaffAccess } from "@/hooks/useStaffAccess";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

type AccessLevel = "staff" | "manager" | "owner";

interface DashboardAccessRouteProps {
  children: React.ReactNode;
  level: AccessLevel;
}

/**
 * Route guard for dashboard pages that enforces role-based access.
 * Uses backend-verified permissions from the verify-dashboard-access edge function.
 * 
 * Access levels:
 * - "staff": Allows owner, manager, or staff
 * - "manager": Allows owner or manager (blocks staff)
 * - "owner": Allows owner only (blocks staff and manager)
 */
export function DashboardAccessRoute({ children, level }: DashboardAccessRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const { 
    accessType, 
    loading: staffLoading, 
    hasAccess,
    error 
  } = useStaffAccess();

  const isLoading = authLoading || staffLoading;

  // Log access check for debugging
  console.log("[DashboardAccessRoute] Check:", { 
    level, 
    accessType, 
    hasAccess, 
    isLoading, 
    error,
    userId: user?.id 
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!user) {
    console.log("[DashboardAccessRoute] Not authenticated, redirecting to login");
    return <Navigate to="/auth?mode=login" replace />;
  }

  // No business access at all
  if (!hasAccess) {
    console.log("[DashboardAccessRoute] No business access, redirecting to home");
    return <Navigate to="/" replace />;
  }

  // Check access based on level requirement
  const hasRequiredAccess = (() => {
    switch (level) {
      case "staff":
        // Staff level: owner, manager, or staff can access
        return accessType === "owner" || accessType === "manager" || accessType === "staff";
      case "manager":
        // Manager level: owner or manager can access
        return accessType === "owner" || accessType === "manager";
      case "owner":
        // Owner level: only owner can access
        return accessType === "owner";
      default:
        return false;
    }
  })();

  if (!hasRequiredAccess) {
    console.log(`[DashboardAccessRoute] Access denied - required: ${level}, have: ${accessType}`);
    // Redirect to dashboard overview if access denied
    return <Navigate to="/dashboard" replace />;
  }

  console.log(`[DashboardAccessRoute] Access granted - ${accessType} for ${level} route`);
  return <>{children}</>;
}
